import React, { useEffect, useRef, useState } from 'react';
import './RichText.css';
import '../../components/Text.css'
import {
  Editor,
  EditorState,
  RichUtils,
  convertFromRaw,
  Modifier,
  convertToRaw
} from 'draft-js';

// Utility functions
import { cleanData } from './cleanData';
import { convertToRawFormat } from './convertToRawFormat';
import { blockStyles, colorStyles, draftJSInlineStyles, weights } from './data';
import { getActiveStyles } from './getActiveStyles';
import { createDOMElements } from './createDOMElements';

// Themeable Components
import { HStack, VStack } from '../../components/Stack';
import { Button } from '../../components/Button';
import { Cell } from '../../components/Cell';
import { Body } from '../../components/Text';
import { Icon } from '../../components/Icon';
import { Sheet } from '../../components/Sheet';
import { handleSelection } from './handleSelection';


interface RichTextProps {
  initialState?: any,

  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /** React object that can be used in your UI */
  outputReact?: (elements: JSX.Element[]) => void,

  /** Raw JSON format from the component */
  outputRaw?: (data: any) => void;
}

const RichText: React.FC<RichTextProps> = ({
  initialState,
  outputReact,
  outputRaw,
  onBlur
}) => {

  // Sets the initial state of the editor
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0, visible: false });

  // Sheets
  const [colorSheet, setColorSheet] = useState(false);
  const [blockStyleSheet, setBlockStyleSheet] = useState(false);
  const [weightSheet, setWeightSheet] = useState(false);
  // Sheet refs
  const colorButtonRef = useRef(null);
  const blockStyleButtonRef = useRef(null);
  const weightButtonRef = useRef(null);

  const [currentBlock, setCurrentBlock] = useState<string | undefined>(undefined);
  const [currentInlineStyles, setCurrentInlineStyles] = useState({
    weights: [],
    colors: [],
  });
  const parentRef = useRef(null);


  useEffect(() => {
    if (initialState) {
      setEditorState(EditorState.createWithContent(convertFromRaw(convertToRawFormat(initialState))))
    }
  }, [])



  useEffect(() => {
    // Only run output functions when editorState is initialized with actual content
    const currentContent = editorState.getCurrentContent();
    if (!currentContent.hasText()) return; // Skip if the content is empty

    setCurrentBlock(
      currentContent.getBlockForKey(editorState.getSelection().getStartKey())?.getType() || undefined
    );

    // @ts-ignore
    setCurrentInlineStyles(getActiveStyles(editorState));

    const rawContent = cleanData(convertToRaw(currentContent));
    outputRaw && outputRaw(rawContent);
    outputReact && outputReact(createDOMElements(rawContent));
  }, [editorState]);

  // Sets current block type
  const setBlockType = (blockStyle: string) => { setEditorState(RichUtils.toggleBlockType(editorState, blockStyle)); }

  // Block classes
  const blockClasses = (block: any) => {
    return block.type
  };

  // const toggleInlineStyle = (inlineStyle: string) => {
  //   setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  // };

  const swapInlineColor = (color: string) => {
    // First create an object then set the object
    let newContentState = editorState.getCurrentContent();
    // Newly created object removes ALL colors
    currentInlineStyles.colors.forEach(style => { newContentState = Modifier.removeInlineStyle(newContentState, editorState.getSelection(), style); });
    // Newly created object adds back in appropriate color (except of text-primary)
    if (color !== 'textPrimary') {
      newContentState = Modifier.applyInlineStyle(newContentState, editorState.getSelection(), color);
    }
    // Cloned object is then used to set the new state of the editor
    setEditorState(EditorState.push(editorState, newContentState, 'change-inline-style'))
  };

  const swapInlineWeight = (weight: string) => {
    let newContentState = editorState.getCurrentContent();
    // Remove all existing weight styles (Default, Regular, Bold)
    currentInlineStyles.weights.forEach(style => { newContentState = Modifier.removeInlineStyle(newContentState, editorState.getSelection(), style); });

    // Apply the new weight if it's not "Default"
    if (weight !== 'default') {
      newContentState = Modifier.applyInlineStyle(newContentState, editorState.getSelection(), weight);
    }

    // Push the updated content state to the editor
    setEditorState(EditorState.push(editorState, newContentState, 'change-inline-style'));
  };



  const getCurrentColor = () => {
    if (currentInlineStyles.colors.length === 1) {
      return colorStyles.find((s) => s.nameCamel === currentInlineStyles.colors[0])
    } else {
      return null
    }
  }

  const getCurrentWeight = () => {
    if (currentInlineStyles.weights.length === 1) {
      return weights.find((s) => s.class === currentInlineStyles.weights[0])
    } else {
      return null
    }
  }




  return (
    <>
      <VStack ref={parentRef} style={{
        position: 'relative'
      }}>
        {toolbarPosition.visible && (
          <HStack
            align='left middle group'
            width='fill'
            style={{
              position: 'absolute',
              top: toolbarPosition.top - 24,
              left: toolbarPosition.left,
              background: 'rgb(var(--ts-color-background-elevated))',
              padding: '0px var(--ts-space-h-extra)',
              border: '1px solid rgba(var(--ts-color-border), var(--ts-color-border-alpha))',
              borderRadius: 'var(--ts-radius-large-control)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              zIndex: 1,
            }}
          >
            <Button
              small
              width='hug'
              icon1='Text'
              text1={currentBlock === 'unstyled' ? 'Body' : currentBlock?.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([a-zA-Z])([0-9])/g, '$1 $2').replace(/^./, function(str) { return str.toUpperCase() })}
              variant='quaternary'
              ref={blockStyleButtonRef}
              onClick={() => setBlockStyleSheet(true)}
            />

            <Sheet
              anchor={blockStyleButtonRef}
              onDismiss={() => setBlockStyleSheet(false)}
              active={blockStyleSheet}
            >
              {blockStyles.map((style) => (
                <Cell
                  onClick={() => {
                    setBlockType(style.cameCaseName);
                    setBlockStyleSheet(false)
                  }}>
                  <HStack>
                    <Icon name={currentBlock === style.cameCaseName ? 'Check' : 'Blank'} />
                    <Body>{style.blockStyleName}</Body>
                  </HStack>
                </Cell>
              ))}
            </Sheet>

            <Button
              small
              width='hug'
              icon1='Bullet'
              icon1ColorCustom={getCurrentColor()?.inlineStyle}
              text1={
                getCurrentColor()
                  // @ts-ignore
                  ? getCurrentColor().colorName
                  : currentInlineStyles.colors.length > 1
                    ? 'Mixed'
                    : 'Default'
              }
              variant='quaternary'
              ref={colorButtonRef}
              onClick={() => setColorSheet(true)}
            />

            <Sheet onDismiss={() => setColorSheet(false)} active={colorSheet} anchor={colorButtonRef}>
              {colorStyles.map((style) => (
                <Cell onClick={() => { swapInlineColor(style.nameCamel); setColorSheet(false) }} >
                  <HStack align='middle'>
                    {/* @ts-ignore */}
                    <Icon name='Bullet' color={style.colorVariable} />
                    <Body> {style.colorName} </Body>
                    {style.nameCamel === currentInlineStyles.colors.find((s: any) => s === style.nameCamel) && <Icon name='Check' />}
                  </HStack>
                </Cell>
              ))}
            </Sheet>

            <Button
              small
              width='hug'
              icon1='Bold'
              text1={
                getCurrentWeight()
                  // @ts-ignore
                  ? getCurrentWeight().name
                  : currentInlineStyles.weights.length > 1
                    ? 'Mixed'
                    : 'Default'
              }
              variant='quaternary'
              ref={weightButtonRef}
              onClick={() => setWeightSheet(true)}
            />
            <Sheet onDismiss={() => setWeightSheet(false)} active={weightSheet} anchor={weightButtonRef}>
              {weights.map((weight) => (
                <Cell onClick={() => { swapInlineWeight(weight.class); setWeightSheet(false) }} >
                  <HStack align='middle'>
                    {/* <Icon name='Bullet' color={style.colorVariable} /> */}
                    <Body> {weight.name} </Body>
                    {weight.class === currentInlineStyles.weights.find((s: any) => s === weight.class) && <Icon name='Check' />}
                  </HStack>
                </Cell>
              ))}
            </Sheet>

          </HStack>
        )}
        <VStack align='group'>
          <Editor
            editorState={editorState}
            onChange={(e) => {
              setEditorState(e);
              handleSelection(toolbarPosition, setToolbarPosition, parentRef)
            }}
            customStyleMap={draftJSInlineStyles}
            blockStyleFn={blockClasses}
            // handleKeyCommand={handleKeyCommand}
            placeholder='Enter text'
            onBlur={onBlur}
          />
        </VStack>
      </VStack>
    </>
  )
}

export { RichText };
